import React, { useMemo, useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import Input from "../../controls/Input";
import {
  buildPriceSplitPayload,
  getSplitRemainder,
  getSplitUnitLabel,
} from "../../../shared/orderItemPriceSplits";

export default function OrderItemPriceSplitModal({
  orderItem,
  split = null,
  onConfirm,
  setOpen,
}) {
  const [values, setValues] = useState({
    quantity: split?.quantity ?? "",
    unit_price: split?.unit_price ?? "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const unitLabel = getSplitUnitLabel(orderItem);
  const available = useMemo(
    () => getSplitRemainder(orderItem, split?.id ?? null),
    [orderItem, split],
  );

  const change = (event) => {
    setValues((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
    setErrors((current) => ({ ...current, [event.target.name]: null }));
  };

  const submit = async () => {
    const quantity = Number(values.quantity);
    const unitPrice = Number(values.unit_price);
    const nextErrors = {};
    if (!Number.isFinite(quantity) || quantity <= 0) {
      nextErrors.quantity = "Quantity must be greater than zero.";
    } else if (quantity > available + 0.0001) {
      nextErrors.quantity = `Quantity cannot exceed ${available} ${unitLabel}.`;
    }
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      nextErrors.unit_price = "Unit price cannot be negative.";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      await onConfirm(buildPriceSplitPayload(values));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-900">
        {split ? "Edit price split" : "Add price split"}
      </h3>
      <p className="mt-1 text-sm text-slate-500">
        {orderItem.product_name} · Available {available} {unitLabel}
      </p>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Quantity"
          labelOn
          name="quantity"
          type="number"
          min="0"
          step="0.0001"
          value={values.quantity}
          onChange={change}
          suffix={` ${unitLabel}`}
          error={errors.quantity}
          disabled={submitting}
        />
        <Input
          label="Unit price"
          labelOn
          name="unit_price"
          type="number"
          min="0"
          step="0.0001"
          value={values.unit_price}
          onChange={change}
          prefix="$"
          suffix={` /${unitLabel}`}
          error={errors.unit_price}
          disabled={submitting}
        />
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button
          type="button"
          disabled={submitting}
          onClick={() => setOpen({ childComponent: null, open: false })}
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={submit}
          className="inline-flex min-w-[6rem] items-center justify-center gap-2 rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-600 disabled:cursor-wait disabled:opacity-70"
        >
          {submitting ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
          ) : null}
          {submitting ? "Saving…" : split ? "Update" : "Add split"}
        </button>
      </div>
    </div>
  );
}
